import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSegmentNavigation } from "./useSegmentNavigation";

interface UseSectionsProps<T> {
  defaultSection: T;
  basePath?: string;
  filtersToKeep?: string[];
  filters?: Record<string, string | number | undefined>;
  onSectionChange?: (curr: T) => void;
  getDeep?: (curr: T) => number;
  sectionKey?: string;
}

export function useSections<T>({
  basePath,
  defaultSection,
  filters,
  filtersToKeep,
  sectionKey = "section",
  getDeep,
  onSectionChange,
}: UseSectionsProps<T>) {
  const { [sectionKey]: currentSection } = useParams();
  const { pathname } = useLocation();
  const { addPathSegment, changeLastPathSegment } = useSegmentNavigation();

  const section = useMemo(() => (currentSection || defaultSection) as T,
  [currentSection]);

  useEffect(() => {
    onSectionChange?.(section);
  }, [section]);

  useEffect(() => {
    let path = null;

    if (basePath && pathname === `/${basePath}`) {
      path = `/${defaultSection}`
    } else {
      if (!currentSection) {
        path = defaultSection;
      }
    }
    onSectionChange?.(section);

    path && addPathSegment(path as string);
  }, []);

  const handleSectionChange = (id: T) => {
    if (id !== section) {
      const params = filtersToKeep?.reduce((acc: Record<string, string>, key) => {
        if (filters?.[key]) {
          acc[key] = filters[key] as string;
        }
        return acc;
      }, {});

      changeLastPathSegment(
        id as string,
        params,
        getDeep?.(currentSection as T) || 1,
      );
    }
  };

  return { section, handleSectionChange };
};
